/** ScheduledTaskList — dense table of scheduled tasks with row-action menu.
 *
 * Renders target-type/name/owning-matter/frequency(+next-run stacked)/
 * last-outcome/status columns plus a single 设置 dropdown per row holding
 * 暂停|恢复 / 立即执行 / 编辑 / 删除 (action set frozen width — grows
 * inside the menu, not the column). Pause, resume, run-now and delete are
 * self-contained mutations (like TimeEntriesListTab's inline stop/delete);
 * 编辑 bubbles up so the owning page can open the shared form modal in
 * edit mode. Table scrolls horizontally below its min width (laptop fit).
 *
 * AI 任务中心（agent_run 目标 / once 频率）：
 *  - 名称副行显示任务目标摘要（空目标显示「按模板内置目标」）；
 *  - 最近结果与状态以关联 agent run 的六态为准（audit 行只代表派发完成）；
 *  - once 行：频率「单次」（无下次行）、菜单仅 查看执行 + 删除，
 *    行点击直达 /agent-runs/:agentRunId 执行视图；周期行为现状。 */

import { Button, Dropdown, Modal, Table, Tag, Tooltip, Typography, message } from "antd";
import type { MenuProps } from "antd";
import { useNavigate } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PauseCircleOutlined,
  CaretRightOutlined,
  RocketOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  usePauseScheduledTask,
  useResumeScheduledTask,
  useRunScheduledTaskNow,
  useDeleteScheduledTask,
} from "../../api/queries";
import {
  AGENT_RUN_STATUS_LABELS,
  AGENT_RUN_STATUS_TAG_COLORS,
} from "../../types/agentRun";
import {
  SCHEDULED_TASK_FREQUENCY_LABELS,
  SCHEDULED_TASK_RUN_STATUS_LABELS,
  SCHEDULED_TASK_STATUS_LABELS,
  SCHEDULED_TASK_TARGET_TYPE_LABELS,
  type ScheduledTask,
} from "../../types/scheduledTask";
import { formatDateTime } from "../../utils/date";
import EmptyState from "../common/EmptyState";
import { SkeletonGrid } from "../common/Skeleton";
import {
  RUN_STATUS_TAG_COLOR,
  STATUS_TAG_COLOR,
  TARGET_TYPE_ICON,
  TARGET_TYPE_TAG_COLOR,
  describeTaskActionError,
} from "./constants";

const { Text } = Typography;

interface ScheduledTaskListProps {
  tasks: ScheduledTask[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onSelect: (task: ScheduledTask) => void;
  onEdit: (task: ScheduledTask) => void;
  onCreate: () => void;
  hasActiveFilters: boolean;
}

/** once 任务（agent_run 单次）：定义行只是审计载体，交互全部转向执行视图。 */
function isOnceTask(task: ScheduledTask): boolean {
  return task.frequency === "once";
}

export default function ScheduledTaskList({
  tasks,
  isLoading,
  isError,
  onRetry,
  onSelect,
  onEdit,
  onCreate,
  hasActiveFilters,
}: ScheduledTaskListProps) {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [modalApi, modalHolder] = Modal.useModal();
  const pauseMut = usePauseScheduledTask();
  const resumeMut = useResumeScheduledTask();
  const runNowMut = useRunScheduledTaskNow();
  const deleteMut = useDeleteScheduledTask();

  const handlePause = async (task: ScheduledTask) => {
    try {
      await pauseMut.mutateAsync(task.id);
      messageApi.success("已暂停");
    } catch (err) {
      messageApi.error(describeTaskActionError(err, "任务状态已变化，请刷新后重试"));
    }
  };

  const handleResume = async (task: ScheduledTask) => {
    try {
      await resumeMut.mutateAsync(task.id);
      messageApi.success("已恢复");
    } catch (err) {
      messageApi.error(describeTaskActionError(err, "任务状态已变化，请刷新后重试"));
    }
  };

  const handleRunNow = async (task: ScheduledTask) => {
    try {
      await runNowMut.mutateAsync(task.id);
      messageApi.success("已触发本次执行");
    } catch (err) {
      messageApi.error(describeTaskActionError(err, "该工作流实例存在正在执行的任务，请稍后重试"));
    }
  };

  const handleDelete = async (task: ScheduledTask) => {
    try {
      await deleteMut.mutateAsync(task.id);
      messageApi.success("已删除");
    } catch (err) {
      messageApi.error(describeTaskActionError(err, "删除失败，请刷新后重试"));
    }
  };

  /** once 行直达执行视图；缺 agentRunId（异常数据）回落详情抽屉。 */
  const handleRowClick = (task: ScheduledTask) => {
    if (isOnceTask(task) && task.lastRun?.agentRunId) {
      navigate(`/agent-runs/${task.lastRun.agentRunId}`);
      return;
    }
    onSelect(task);
  };

  /** 菜单内删除：下拉菜单无法包 Popconfirm，改用确认弹窗（同文案）。 */
  const confirmDelete = (task: ScheduledTask) => {
    modalApi.confirm({
      title: isOnceTask(task) ? "确认删除该任务？" : "确认删除该定时任务？",
      content: isOnceTask(task)
        ? "删除后该任务将从列表中移除，已产生的执行历史仍保留以供审计。"
        : "删除后该任务将停止调度并从列表中移除，已产生的执行历史仍保留以供审计。",
      okText: "删除",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: () => handleDelete(task),
    });
  };

  /** 行操作菜单：once 任务 查看执行+删除；周期任务 暂停/恢复 + 立即执行 +
   * 编辑 + 删除。菜单项在菜单内增减，不占列宽。 */
  const rowActionItems = (record: ScheduledTask): MenuProps["items"] => {
    if (isOnceTask(record)) {
      return [
        {
          key: "view-run",
          icon: <EyeOutlined />,
          label: "查看执行",
          disabled: !record.lastRun?.agentRunId,
        },
        { type: "divider" },
        { key: "delete", icon: <DeleteOutlined />, label: "删除", danger: true },
      ];
    }
    return [
      ...(record.status === "active"
        ? [{ key: "pause", icon: <PauseCircleOutlined />, label: "暂停" }]
        : []),
      ...(record.status === "paused"
        ? [{ key: "resume", icon: <CaretRightOutlined />, label: "恢复" }]
        : []),
      {
        key: "run-now",
        icon: <RocketOutlined />,
        label: "立即执行",
        disabled: record.status === "disabled",
      },
      { key: "edit", icon: <EditOutlined />, label: "编辑" },
      { type: "divider" },
      { key: "delete", icon: <DeleteOutlined />, label: "删除", danger: true },
    ];
  };

  const onRowActionClick: (
    task: ScheduledTask,
  ) => NonNullable<MenuProps["onClick"]> = (task) => (info) => {
    switch (info.key) {
      case "view-run":
        if (task.lastRun?.agentRunId) navigate(`/agent-runs/${task.lastRun.agentRunId}`);
        break;
      case "pause":
        void handlePause(task);
        break;
      case "resume":
        void handleResume(task);
        break;
      case "run-now":
        void handleRunNow(task);
        break;
      case "edit":
        onEdit(task);
        break;
      case "delete":
        confirmDelete(task);
        break;
    }
  };

  if (isLoading) return <SkeletonGrid />;

  if (isError) {
    return (
      <EmptyState
        title="加载失败"
        description="无法连接到定时任务服务，请稍后重试。"
        action={
          <Button type="primary" onClick={onRetry}>
            重试
          </Button>
        }
      />
    );
  }

  if (!tasks || tasks.length === 0) {
    return (
      <EmptyState
        title={hasActiveFilters ? "没有匹配的定时任务" : "暂无定时任务"}
        description={
          hasActiveFilters
            ? "尝试调整筛选条件或关键词"
            : "创建一条周期性工作或事项提醒，到点后系统自动执行"
        }
        action={
          <Button type="primary" onClick={onCreate}>
            新建定时任务
          </Button>
        }
      />
    );
  }

  const columns: ColumnsType<ScheduledTask> = [
    {
      title: "类型",
      dataIndex: "targetType",
      key: "targetType",
      width: 100,
      render: (_, record) => (
        <Tag icon={TARGET_TYPE_ICON[record.targetType]} color={TARGET_TYPE_TAG_COLOR[record.targetType]}>
          {SCHEDULED_TASK_TARGET_TYPE_LABELS[record.targetType]}
        </Tag>
      ),
    },
    {
      title: "名称",
      dataIndex: "name",
      key: "name",
      render: (_, record) => {
        const subtitle =
          record.targetType === "agent_run"
            ? record.goal?.trim() || "按模板内置目标"
            : record.targetType === "pipeline_instance"
              ? (record.instanceName ?? "工作流实例已删除")
              : record.targetType === "pipeline_template"
                ? `模板 ${record.templateName ?? "-"} · ${record.matterName ?? "-"}`
                : (record.reminderTitle ?? "-");
        return (
          <div>
            <div style={{ fontWeight: 500, color: "var(--jerry-text-primary)" }}>{record.name}</div>
            <div
              title={record.targetType === "agent_run" ? (record.goal?.trim() || "按模板内置目标") : subtitle}
              style={{
                fontSize: "var(--jerry-font-size-xs)",
                color: "var(--jerry-text-tertiary)",
                maxWidth: 360,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {subtitle}
            </div>
          </div>
        );
      },
    },
    {
      title: "所属事项",
      dataIndex: "matterName",
      key: "matterName",
      render: (v: string | null) => v ?? "-",
    },
    {
      title: "频率",
      key: "frequency",
      render: (_, record) => {
        // 频率 + 下次执行合并为同一列（上下两行）：once 单行「单次」（触发
        // 后不重臂，无下次）；周期任务第二行 仅 active 显示（计算中兜底）。
        const freqText =
          record.frequency === "once"
            ? SCHEDULED_TASK_FREQUENCY_LABELS.once
            : record.frequencyText;
        const nextLine =
          record.frequency !== "once" && record.status === "active"
            ? record.nextRunAt
              ? `下次 ${formatDateTime(record.nextRunAt)}`
              : "下次 计算中"
            : null;
        return (
          <div>
            <div>{freqText}</div>
            {nextLine && (
              <div style={{ fontSize: "var(--jerry-font-size-xs)", color: "var(--jerry-text-tertiary)" }}>
                {nextLine}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "最近结果",
      key: "lastRun",
      render: (_, record) => {
        if (!record.lastRun) return <Text type="secondary">从未执行</Text>;
        // agent_run 目标：执行结果以关联 agent run 的六态为准，audit 行的
        // triggered/completed 只代表「派发完成」。
        if (record.targetType === "agent_run" && record.lastRun.agentRunStatus) {
          const tag = (
            <Tag color={AGENT_RUN_STATUS_TAG_COLORS[record.lastRun.agentRunStatus] ?? "default"}>
              {AGENT_RUN_STATUS_LABELS[record.lastRun.agentRunStatus] ?? record.lastRun.agentRunStatus}
            </Tag>
          );
          return record.lastRun.agentRunSummary ? (
            <Tooltip title={record.lastRun.agentRunSummary}>{tag}</Tooltip>
          ) : (
            tag
          );
        }
        const tag = (
          <Tag color={RUN_STATUS_TAG_COLOR[record.lastRun.status]}>
            {SCHEDULED_TASK_RUN_STATUS_LABELS[record.lastRun.status]}
          </Tag>
        );
        return record.lastRun.status === "failed" && record.lastRun.errorMessage ? (
          <Tooltip title={record.lastRun.errorMessage}>{tag}</Tooltip>
        ) : (
          tag
        );
      },
    },
    {
      title: "状态",
      key: "status",
      render: (_, record) => {
        // once 任务：定义状态恒为 finished（无调度语义），用户关心的执行态
        // 以关联 agent run 六态渲染。
        if (isOnceTask(record) && record.lastRun?.agentRunStatus) {
          return (
            <Tag color={AGENT_RUN_STATUS_TAG_COLORS[record.lastRun.agentRunStatus] ?? "default"}>
              {AGENT_RUN_STATUS_LABELS[record.lastRun.agentRunStatus] ?? record.lastRun.agentRunStatus}
            </Tag>
          );
        }
        const tag = <Tag color={STATUS_TAG_COLOR[record.status]}>{SCHEDULED_TASK_STATUS_LABELS[record.status]}</Tag>;
        return record.status === "disabled" && record.disabledReason ? (
          <Tooltip title={record.disabledReason}>{tag}</Tooltip>
        ) : (
          tag
        );
      },
    },
    {
      title: "操作",
      key: "actions",
      width: 64,
      render: (_, record) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Dropdown
            menu={{ items: rowActionItems(record), onClick: onRowActionClick(record) }}
            trigger={["click"]}
          >
            <Button
              size="small"
              type="text"
              icon={<SettingOutlined />}
              data-testid={`scheduled-task-actions-${record.id}`}
            />
          </Dropdown>
        </span>
      ),
    },
  ];

  return (
    <div data-testid="scheduled-task-list">
      {contextHolder}
      {modalHolder}
      <Table<ScheduledTask>
        rowKey="id"
        columns={columns}
        dataSource={tasks}
        pagination={false}
        scroll={{ x: "max-content" }}
        onRow={(record) => ({
          "data-testid": `scheduled-task-row-${record.id}`,
          style: { cursor: "pointer" },
          onClick: () => handleRowClick(record),
        })}
      />
    </div>
  );
}
